import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type CardVariant = 'default' | 'hi' | 'flat' | 'brand' | 'soft';

/**
 * `<nb-card>` — the design-system surface container (Mockup `.card` + variants).
 *
 * A neutral panel that projects arbitrary content. The base is a `--surface`
 * card with a 1px `--border`, `--r-lg` corners and a quiet `--sh-xs` shadow.
 * `variant` switches the visual treatment; `pad` adds the mockup's 18px inset;
 * `lift` opts into a hover-raise (and a pointer cursor) for clickable cards.
 *
 * Purely presentational — it renders a plain `<div>` and adds no ARIA role of
 * its own, so wrap interactive content (links/buttons) inside as usual.
 *
 * @example
 * <nb-card pad variant="hi">Framhevet innhold</nb-card>
 * <nb-card pad variant="brand">På mørk bakgrunn</nb-card>
 * <nb-card pad lift>Klikkbart kort</nb-card>
 */
@Component({
  selector: 'nb-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'card',
    '[class]': 'classes()',
  },
  template: `<ng-content />`,
  styleUrl: './card.css',
})
export class NbCard {
  /** Variant treatment. `default` = base surface; see `CardVariant`. */
  readonly variant = input<CardVariant>('default');
  /** Adds the mockup's 18px inner padding. */
  readonly pad = input(false);
  /** Hover-raise + pointer cursor for clickable cards. */
  readonly lift = input(false);

  protected readonly classes = computed(() => {
    const c: string[] = ['card'];
    if (this.variant() !== 'default') c.push(this.variant());
    if (this.pad()) c.push('pad');
    if (this.lift()) c.push('lift');
    return c.join(' ');
  });
}
